// =============================================================================
// NAD+ Ingredients V2 Section Component
// =============================================================================
// Same cards as NADIngredients, but the slider only exists below the desktop
// breakpoint — on desktop the cards render as a static CSS grid.

import { Swiper } from 'swiper';
import { Pagination, A11y } from 'swiper/modules';

// Matches the SCSS respond-above(md) breakpoint where the static grid applies
const DESKTOP_BREAKPOINT = 1024;

export class NADIngredientsV2 {
  private element: HTMLElement;
  private swiper: Swiper | null = null;
  private observer: IntersectionObserver | null = null;
  private resizeHandler: () => void;
  private resizeTimeout: number | undefined;

  constructor(element: HTMLElement) {
    this.element = element;
    this.resizeHandler = () => {
      window.clearTimeout(this.resizeTimeout);
      this.resizeTimeout = window.setTimeout(() => this.syncSwiper(), 250);
    };
    this.init();
  }

  private init(): void {
    this.syncSwiper();
    window.addEventListener('resize', this.resizeHandler);
    this.setupAnimation();
  }

  /**
   * Swiper below the desktop breakpoint, static grid above it
   */
  private syncSwiper(): void {
    const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;

    if (isDesktop && this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
      return;
    }

    if (!isDesktop && !this.swiper) {
      const swiperElement = this.element.querySelector<HTMLElement>('.nad-ingredients__swiper');
      if (!swiperElement) return;

      this.swiper = new Swiper(swiperElement, {
        modules: [Pagination, A11y],
        slidesPerView: 1,
        spaceBetween: 12,
        grabCursor: true,
        a11y: {
          enabled: true,
          prevSlideMessage: 'Previous ingredient',
          nextSlideMessage: 'Next ingredient',
          firstSlideMessage: 'This is the first ingredient',
          lastSlideMessage: 'This is the last ingredient',
        },
        breakpoints: {
          320: { slidesPerView: 1, spaceBetween: 12 },
          480: { slidesPerView: 1.15, spaceBetween: 12 },
          768: { slidesPerView: 2, spaceBetween: 16 },
        },
      });
    }
  }

  private setupAnimation(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.nad-ingredients__card');
            cards.forEach((card, index) => {
              setTimeout(() => {
                card.classList.add('animate-in');
              }, index * 100);
            });
            this.observer?.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    this.observer.observe(this.element);
  }

  public destroy(): void {
    window.removeEventListener('resize', this.resizeHandler);
    window.clearTimeout(this.resizeTimeout);
    this.swiper?.destroy(true, true);
    this.swiper = null;
    this.observer?.disconnect();
    this.observer = null;
  }

  static initializeAll(): void {
    document.querySelectorAll<HTMLElement>('.nad-ingredients-v2').forEach((el) => {
      if (!(el as any)._nadComponentInstance) {
        (el as any)._nadComponentInstance = new NADIngredientsV2(el);
      }
    });
  }
}
